import { action } from '@ember/object';
import type Owner from '@ember/owner';

import type { ValidationErrors } from 'webapp-types';

import FormCheckboxGroupComponent, {
	type GroupSelected,
} from 'webapp/components/form/checkbox-group';
import FormSliderComponent from 'webapp/components/form/slider';
import MetricAnomaliesBaseComponent from 'webapp/components/metric-anomalies/edit/base';
import NotificationsDeliveryTargetsComponent from 'webapp/components/notifications/delivery-targets';
import {
	type ChunkArgs as BaseChunkArgs,
	type ChunkInterface,
	type SaveFn,
	type ValidateFn,
} from 'webapp/components/notifications/edit/form/base';
import NotificationsEditFormMetricSetupRowComponent from 'webapp/components/notifications/edit/form/metric-setup-row';
import FormRowComponent from 'webapp/components/notifications/edit/form/row';
import RowErrorComponent from 'webapp/components/row-error';

import type { MetricAnomaly } from 'webapp/models-custom';

type ValidationKeys = 'metric' | 'unit' | 'attribute' | 'frequency' | 'delivery';
export type ValidationErrorsType = ValidationErrors<ValidationKeys>;

interface MainComponentArgs extends BaseChunkArgs {
	model: MetricAnomaly;
	submitInProgress: boolean;
	errors: ValidationErrorsType;
	origin: string;
	setValidateFn?: (fn: ValidateFn) => void;
	setSaveFn?: (fn: SaveFn) => void;
}

interface MainComponentInterface extends ChunkInterface {
	Args: MainComponentArgs;
}

export default class NotificationsEditFormAnomalyMainComponent extends MetricAnomaliesBaseComponent<MainComponentInterface> {
	constructor(owner: Owner, args: MainComponentArgs) {
		super(owner, args);

		this.args.setValidateFn?.(this.validate);
		this.args.setSaveFn?.(this.save);

		// Determine the settings source - either from existing anomaly or default settings
		const settings =
			this.args.model.settings || this.metricAnomaliesService.topItemDefaultSettings;
		this.parseAndSetDefaultState(settings);
	}

	get metricSetup() {
		return this.args.model.metricSetup;
	}

	@action
	override updateDelivery(value: number[]): void {
		super.updateDelivery(value);
		this.args.resetErrorData?.('delivery');
	}

	@action
	override handleGranulationsChange(value: GroupSelected) {
		super.handleGranulationsChange(value);
		this.args.resetErrorData?.('frequency');
	}

	@action
	validate() {
		const errors: ValidationErrorsType = {};

		const model = this.args.model;

		model.validate(); // to cleanup general errors

		if (model.metricSetupKey) {
			if (!model.metricSetup.isFilledSingleOutput) {
				errors.attribute = [{ message: 'Required' }];
				errors.unit = [{ message: 'Required' }];
			}
		} else {
			errors.metric = [{ message: 'Required' }];
		}

		if (!this.selectedDeliveryTypes.length) {
			errors.delivery = [{ message: 'Required' }];
		}

		if (!this.selectedGranulations.length) {
			errors.frequency = [{ message: 'Required' }];
		}

		return errors;
	}

	@action
	save() {
		const model = this.args.model;

		const settings = this.serializeSettings();
		const metricSetup = this.metricSetup;

		model.metricSetupKey = metricSetup.key;
		model.metricSetupSpaceAccessId = metricSetup.spaceAccessId;
		model.metricSetupDimensions = metricSetup.dimensions;
		model.metricSetupFilters = metricSetup.filtersData;
		model.settings = settings;

		if (model.isNew) {
			return model.save().then(() => {
				this.metricAnomaliesService.sendTrackingEventForEnabledAnomaly(model);
			});
		} else {
			return this.metricAnomaliesService.updateMetricAnomaliesSettings(settings, model);
		}
	}

	<template>
		<div class="top-title">Get notified when anomalies are detected for</div>
		{{#if @model.isNew}}
			<NotificationsEditFormMetricSetupRowComponent
				@metricSetup={{@model.metricSetup}}
				@isDisabled={{@submitInProgress}}
				@isMetricDisabled={{true}}
				@errors={{@errors}}
			/>
		{{/if}}
		<FormRowComponent
			@label="With a score higher than"
			@className="row--slider row--label-wide"
		>
			<FormSliderComponent
				@onChange={{this.handleAnomalyScoreChange}}
				@value={{this.anomalyScore}}
				@min={{0}}
				@max={{99}}
				@step={{1}}
				@name="anomalyScore"
			/>
			<p>
				Controls how many data points are flagged as anomalies. By default, about 1% are
				detected. Increase sensitivity to flag more anomalies, decrease it to flag fewer.
			</p>
		</FormRowComponent>

		<FormRowComponent @label="Frequency" @className="row--checkboxes row--label-wide">
			<FormCheckboxGroupComponent
				@name="granulations"
				@options={{this.defaultGranulations}}
				@selected={{this.selectedGranulations}}
				@isDisabled={{false}}
				@onChange={{this.handleGranulationsChange}}
				@theme="blue"
			/>
			<RowErrorComponent @items={{@errors.frequency}} />
		</FormRowComponent>

		<NotificationsDeliveryTargetsComponent
			@deliveryTypes={{this.deliveryTypes}}
			@selected={{this.selectedDeliveryTypes}}
			@onSelectionChange={{this.updateDelivery}}
			@slackChannels={{this.selectedSlackChannels}}
			@onSlackChannelsChange={{this.updateSlackChannels}}
			@extraClass="row--label-wide"
		>
			<RowErrorComponent @items={{@errors.delivery}} />
		</NotificationsDeliveryTargetsComponent>
	</template>
}
